/**
 * REF-2 — the Refund Log table's OWN server-driven read path (GET /sales/live/v2/refund-log
 * with `search` / `type` / `reason` / `staff` / `channel` / `sortBy` / `sortDir` / `page` / `size`).
 *
 * Deliberately separate from `useRefundLogPeriod` (the whole-period, unfiltered fetch that
 * feeds the context strip and the Reasons/Staff cards' fallback data): that fetch and this
 * one both hit `/refund-log`, but with different params, and the Redux slice `useRefundLogPeriod`
 * reads from holds only ONE response at a time — sharing it here would make the table's own
 * search/sort/paging silently blow away the cards' whole-period data (or vice versa) on every
 * refetch. Self-fetching locally, the same way EOD's `TaxesTableSection` owns its own
 * page/size/sort state outside Redux, keeps the two reads from ever colliding.
 *
 * Search is expected to already be debounced by the caller — this hook fires a request on
 * every change to its args, same as every other real-data REF hook.
 */
import { useEffect, useRef, useState } from 'react';
import { useBranch } from '../../../context/BranchContext';
import { useAuth } from '../../../context/AuthContext';
import { getRefundLogApi } from '../../../redux/reports/clickHouse/clickHouseApi';
import type { OrderSource } from '../../../redux/reports/clickHouse/clickHouseApi';
import { REFUND_TYPE_LABEL, type RefundRecord, type RefundSalesSource, type RefundType } from './refundModel';
import { refundLogItemsToRecords } from './refundLogRow';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token']) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const toOrderSource = (source: RefundSalesSource): OrderSource | undefined =>
    source === 'direct' ? 'direct' : source === 'thirdparty' ? 'thirdparty' : undefined;

export interface UseRefundLogTableQueryArgs {
    startDate?: string;
    endDate?: string;
    source: RefundSalesSource;
    /** Already debounced by the caller. Empty string sends no `search` param. */
    search: string;
    /** A `/refund-log` response field name — see `REFUND_LOG_SORT_FIELD` in refundLogQuery.ts. */
    sortBy: string;
    sortDir: 'asc' | 'desc';
    type: RefundType[];
    /** Literal reason text — matches `RefundLogFilters.reasons`. */
    reason: string[];
    staff: string[];
    channel: string[];
    /** 1-indexed, matching the API (page=1 is the first page). */
    page: number;
    /** Rows per page. */
    size: number;
}

/**
 * Period-wide totals for the CURRENT filtered/searched set, straight off the response
 * root — independent of which page happens to be loaded, so the Refund Log's own Total
 * row (and its export) never need to re-sum whatever page the table is currently showing.
 */
export interface RefundLogTotals {
    item: number;
    tax: number;
    tipsAndGratuity: number;
    convenienceFee: number;
    deliveryFee: number;
    serviceCharge: number;
    cardProcessingFee: number;
    refunded: number;
}

export interface UseRefundLogTableQueryResult {
    records: RefundRecord[];
    totals: RefundLogTotals;
    /** Matching rows across every page — drives the pager and the "Showing X–Y of Z" line. */
    totalElements: number;
    totalPages: number;
    loading: boolean;
    failure: boolean;
    retry: () => void;
}

const EMPTY_TOTALS: RefundLogTotals = {
    item: 0, tax: 0, tipsAndGratuity: 0,
    convenienceFee: 0, deliveryFee: 0, serviceCharge: 0, cardProcessingFee: 0,
    refunded: 0,
};

export function useRefundLogTableQuery({ startDate, endDate, source, search, sortBy, sortDir, type, reason, staff, channel, page, size }: UseRefundLogTableQueryArgs): UseRefundLogTableQueryResult {
    const { selectedBranch: branch } = useBranch();
    const { auth } = useAuth();
    const locationId = branch?.id ?? branch?.locationId ?? '';
    const token = auth ? findToken(auth) : null;
    const orderSource = toOrderSource(source);
    // The response's `refundType` field is 'Full' | 'Item' | 'Fee' — the same strings
    // REFUND_TYPE_LABEL already renders, so the filter is sent in the API's own casing.
    const apiType = type.map((t) => REFUND_TYPE_LABEL[t]);

    const [records, setRecords] = useState<RefundRecord[]>([]);
    const [totals, setTotals] = useState<RefundLogTotals>(EMPTY_TOTALS);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [failure, setFailure] = useState(false);
    const fetchIdRef = useRef(0);

    const fetch = () => {
        if (!locationId || !token) return;
        const id = ++fetchIdRef.current;
        setLoading(true);
        setFailure(false);
        getRefundLogApi({ locationId, startDate, endDate, orderSource, search: search || undefined, type: apiType, reason, staff, channel, sortBy, sortDir, page, size, token })
            .then((res: any) => {
                if (id !== fetchIdRef.current) return;
                const content = Array.isArray(res) ? res : (res?.content ?? res?.data ?? []);
                setRecords(refundLogItemsToRecords(content));
                // Period-level totals for the current filtered/searched result — sit
                // alongside `content` at the response root, not on `content[0]`, and cover
                // the WHOLE matching set regardless of `page`/`size`.
                setTotals(Array.isArray(res) ? EMPTY_TOTALS : {
                    item: Number(res?.totalItemsAmt) || 0,
                    tax: Number(res?.totalTaxAmt) || 0,
                    tipsAndGratuity: Number(res?.totalTipsAndGratAmt) || 0,
                    convenienceFee: Number(res?.totalConvenienceFeeAmt) || 0,
                    deliveryFee: Number(res?.totalDeliveryAmt) || 0,
                    serviceCharge: Number(res?.totalServiceChargeAmt) || 0,
                    cardProcessingFee: Number(res?.totalCardProcessingFeeAmt) || 0,
                    refunded: Number(res?.totalRefundedAmt) || 0,
                });
                setTotalElements(Array.isArray(res) ? content.length : (Number(res?.totalElements) || 0));
                setTotalPages(Array.isArray(res) ? 1 : (Number(res?.totalPages) || 0));
                setLoading(false);
            })
            .catch(() => {
                if (id !== fetchIdRef.current) return;
                setRecords([]);
                setTotals(EMPTY_TOTALS);
                setTotalElements(0);
                setTotalPages(0);
                setFailure(true);
                setLoading(false);
            });
    };

    // Arrays are joined for the dependency list so a same-content filter selection (a new
    // array reference every render) doesn't refetch — only an actual change in membership does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetch(); }, [locationId, token, startDate, endDate, orderSource, search, sortBy, sortDir, apiType.join(','), reason.join(','), staff.join(','), channel.join(','), page, size]);

    return { records, totals, totalElements, totalPages, loading, failure, retry: fetch };
}
